<template>
  <div class="m-footer">
    <div class="ft-left-box">
      <span class="font-big-m">FW {{ fw.week }}.{{ fw.day }}</span>
    </div>
    <div class="ft-right-box">
      <div class="button-bar">
        <a-button
          v-for="item in btnItem"
          :key="item.id"
          ghost
          @click="openAndonTypeModal(item)"
        >
          <icon-font style="color: #ffffff" :type="item.iconName" />
          <span>{{ item.name }}</span>
          <i v-if="item.badge != 0" class="badge badge-assertive">
            <span>{{ item.badge }}</span>
          </i>
        </a-button>
      </div>
    </div>
    <a-modal
      v-model:visible="visible"
      title="Andon Summary"
      width="80%"
      wrap-class-name="big-modal"
      @cancel="onModalCancel"
    >
      <template #footer>
        <a-button @click="queryData">
          <template #icon>
            <undo-outlined />
          </template>
          Refresh
        </a-button>
        <a-button type="primary" @click="visible = false">
          <template #icon>
            <close-outlined />
          </template>
          Close
        </a-button>
      </template>
      <a-table
        :columns="columns"
        :row-key="(record) => record.adnId"
        :data-source="dataSource"
        :loading="loading"
        size="small"
        @change="handleTableChange"
      >
        <template
          #customFilterDropdown="{
            setSelectedKeys,
            selectedKeys,
            confirm,
            clearFilters,
          }"
        >
          <div style="padding: 8px">
            <div style="margin-bottom: 8px; display: block">
              <a-range-picker
                :value="selectedKeys"
                :allowClear="false"
                ref="rangePickerRef"
                @change="(e) => setSelectedKeys(e ? e : [])"
                @pressEnter="confirm()"
                format="YYYY-MM-DD"
              />
            </div>
            <a-button
              type="primary"
              size="small"
              style="width: 90px; margin-right: 8px"
              @click="confirm()"
            >
              Search
            </a-button>
            <a-button size="small" style="width: 90px" @click="clearFilters()">
              Reset
            </a-button>
          </div>
        </template>

        <template #bodyCell="{ column, record }">
          <template v-if="column.dataIndex === 'stopWS'">
            <warning-filled
              v-if="record.stopWS"
              style="
                 {
                  font-size: 22px;
                  color: red;
                }
              "
            />
            <span v-else>-</span>
          </template>
          <template v-if="column.dataIndex === 'action'">
            <a-button
              type="primary"
              size="small"
              ghost
              @click="openEditModal(record, 1)"
              :disabled="record.status !== 'open'"
            >
              Response
            </a-button>
            <a-divider type="vertical" />
            <a-button
              type="primary"
              size="small"
              :disabled="record.status === 'closed'"
              ghost
              @click="openEditModal(record, 2)"
            >
              Close
            </a-button>
          </template>
        </template>
      </a-table>
    </a-modal>
    <a-modal
      v-model:visible="editAndonModalVisible"
      width="80%"
      :title="editAndonTitle"
      @ok="onEditAndonSubmit"
    >
      <edit-andon
        ref="andonModalRef"
        :current-row="currentRow"
        :job-list="jobList"
        @responseSuccess="onEditAndonSuccess"
      ></edit-andon>
    </a-modal>
  </div>
</template>

<script>
  import {
    andonsummary,
    getAndontypelist,
    getDashboardRegionlist,
    getJobList,
  } from '@/api/supervisor-landing'
  import { parseTime } from '@/utils'
  import { message } from 'ant-design-vue'
  import {
    WarningFilled,
    UndoOutlined,
    CloseOutlined,
  } from '@ant-design/icons-vue'
  import dayjs from 'dayjs'
  import EditAndon from '@/components/edit-andon'

  const stopWSList = [
    {
      text: 'true',
      value: 1,
    },
    {
      text: 'false',
      value: 2,
    },
  ]
  const statusList = [
    { value: 1, text: 'open' },
    { value: 2, text: 'closed' },
    { value: 3, text: 'responsed' },
  ]
  export default {
    inject: ['getCurrentPage', 'getUiid'],
    props: {
      fw: {
        type: Object,
        default() {
          return {}
        },
      },
      btnItem: {
        type: Array,
        default() {
          return []
        },
      },
    },
    components: {
      WarningFilled,
      EditAndon,
      UndoOutlined,
      CloseOutlined,
    },
    watch: {
      visible(v) {
        this.$emit('andonModalChange', v)
      },
    },
    data() {
      return {
        actionType: 1,
        jobList: [],
        categoryList: [],
        regionList: [],
        filteredInfo: {},
        visible: false,
        editAndonModalVisible: false,
        currentRow: null,
        dataSource: [],
        editAndonTitle: '',
      }
    },
    computed: {
      columns() {
        const filtered = this.filteredInfo.value || {}
        return [
          {
            title: 'No',
            dataIndex: 'no',
            key: 'no',
            customRender({ index }) {
              return index + 1
            },
            width: 60,
          },
          {
            title: 'Category',
            dataIndex: 'andonType',
            minWidth: 100,
            key: 'andonType',
            sorter: (a, b) =>
              a.andonType.charCodeAt() - b.andonType.charCodeAt(),
            filters: this.categoryList,
            filterMultiple: false,
            filteredValue: filtered.andonType || null,
          },
          {
            title: 'Andon Description',
            dataIndex: 'description',
            key: 'description',
            minWidth: 200,
          },
          {
            title: 'Job ID',
            dataIndex: 'jobId',
            minWidth: 120,
          },
          {
            title: 'Region Code',
            dataIndex: 'regionCode',
            minWidth: 120,
            key: 'regionCode',
            filters: this.regionList,
            filterMultiple: false,
            filteredValue: filtered.regionCode || null,
          },
          {
            title: 'Created On',
            dataIndex: 'createOn',
            minWidth: 120,
            key: 'createOn',
            customRender({ text }) {
              return parseTime(text, '{m}/{d} {h}:{i}')
            },
            customFilterDropdown: true,
            filteredValue: filtered.createOn || null,
          },
          {
            title: 'Responsed By',
            dataIndex: 'responseBy',
            key: 'responseBy',
            minWidth: 120,
          },
          {
            title: 'Line Stop',
            dataIndex: 'stopWS',
            minWidth: 100,
            key: 'stopWS',

            filters: stopWSList,
            filterMultiple: false,
            filteredValue: filtered.stopWS || null,
          },
          {
            title: 'Status',
            dataIndex: 'status',
            minWidth: 80,
            key: 'status',
            filters: statusList,
            defaultFilteredValue: [1, 3],
            filterMultiple: true,
            filteredValue: filtered.status || null,
          },
          {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            minWidth: 200,
          },
        ]
      },
    },
    methods: {
      onModalCancel() {
        this.filteredInfo.value = {
          andonType: [],
          status: [],
          createOn: [],
        }
      },
      async openAndonTypeModal(item) {
        const loading = message.loading('Loading..', 0)
        try {
          const start = dayjs().subtract(7, 'day')
          const end = dayjs()
          this.filteredInfo.value = {
            andonType: [item.parentAndonType],
            status: [1, 3],
            createOn: [start, end],
          }
          await this.queryData()
          await this._getDashboardRegionlist()
          await this._getAndontypelist()

          this.visible = true
          loading()
        } catch (e) {
          loading()
        }
      },

      async _getAndontypelist() {
        const { body } = await getAndontypelist({
          id: this.getCurrentPage(),
          uiid: this.getUiid(),
        })
        this.categoryList = body.map((item) => {
          return {
            text: item.name,
            value: item.value,
          }
        })
        return Promise.resolve()
      },
      async _getDashboardRegionlist() {
        const { body } = await getDashboardRegionlist({
          id: this.getCurrentPage(),
          uiid: this.getUiid(),
        })
        this.regionList = body.map((item) => {
          return {
            text: item.name,
            value: item.value,
          }
        })

        return Promise.resolve()
      },
      async queryData() {
        this.loading = true
        try {
          let end = 0
          let start = 0
          const { createOn, regionCode, stopWS, status, andonType } =
            this.filteredInfo.value
          if (createOn && createOn.length) {
            end = dayjs(createOn[1]).valueOf() + 24 * 60 * 60 * 1000 - 1
            start = dayjs(createOn[0]).valueOf()
          }

          const raw = {
            id: this.getCurrentPage(),
            uiid: this.getUiid(),
            end,
            start,
            andontype: andonType ? andonType.join() : '',
          }
          const { body } = await andonsummary(raw)
          const filterFn = (list) => {
            let ret = [...list]
            if (regionCode) {
              ret = ret.filter((item) => item.regionCode === regionCode[0])
            }
            if (stopWS) {
              const f = stopWSList.find((item) => item.value === stopWS[0])
              ret = ret.filter((item) => item.stopWS.toString() === f.text)
            }
            if (status) {
              let fa = statusList.filter((item) => {
                return status.includes(item.value)
              })

              const m = fa.map((item) => item.text)
              ret = ret.filter((item) => m.includes(item.status))
            }

            return ret
          }
          this.dataSource = filterFn(body)
          this.loading = false
        } catch (e) {
          this.loading = false
        }
        return Promise.resolve()
      },
      handleTableChange(pag, filters) {
        this.filteredInfo.value = filters

        this.queryData()
      },
      handleSearch(selectedKeys, confirm) {
        confirm()
      },

      handleReset(clearFilters) {
        clearFilters()
      },
      // 打开 response Andon
      openEditModal(item, type) {
        this.editAndonTitle = type === 1 ? 'Response Andon' : 'Close Andon'
        this.actionType = type
        this.currentRow = item
        this._getJobList(item.regionCode.replace(/\+/g, '%2b'))
        this.editAndonModalVisible = true
      },
      onEditAndonSubmit() {
        this.$refs.andonModalRef.submit(this.actionType)
      },
      onEditAndonSuccess() {
        this.editAndonModalVisible = false
        message.success('Success!')
        this.queryData()
      },
      async _getJobList(regionCode) {
        try {
          const d = await getJobList({ regionCode })
          this.jobList = d
        } catch (e) {
          this.jobList = []
        }
      },
    },
  }
</script>

<style lang="less" scoped>
  @import '~@/styles/variables.less';
  .m-footer {
    background: @subMaster;
    color: @white6;
    height: 50px;
    transition: all 0.1s;
    padding: 0;
    border: 0;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .font-big-m {
    font-size: 20px;
  }
  .ft-right-box .button-bar > button {
    border: 0;
    border-right: 1px solid @white1;
    min-width: 80px;
    position: relative;
    i {
      display: block;
      line-height: 1;
    }
    &:last-child {
      border-right: none;
    }
  }

  .ft-left-box {
    > span {
      padding: 0 15px;
      line-height: 35px;
      color: @white6;
    }
    .btn-has-right-icon {
      color: @white8;
      border-right: 1px solid @white1;
      padding-right: 10px;
    }
    a.button {
      border-right: 1px solid @white1;
      display: inline-block;
      padding-right: 15px;
      color: @white6;
    }
  }
  .ft-label {
    padding-right: 10px;
  }
  .ft-right-box {
    .button-bar {
      justify-content: flex-end;
      :deep(.ant-btn) {
        padding: 0 10px;
        color: @white6;
        height: auto;
      }
      :deep(.anticon) {
        display: block;
        color: #fff;
        font-size: 20px;
      }
    }

    i.badge {
      position: absolute;
      top: -3px;
      right: 1px;
      font-size: 12px;
      background: none;
      width: 30px;
      height: 30px;
      text-align: right;
      padding: 0 5px 0 0;
      display: block;
      line-height: 1;
      color: #fff;
      font-style: normal;
      display: flex;
      align-items: center;
      font-weight: 700;
      &:before {
        content: '';
        display: block;
        width: 0;
        height: 0;
        border-color: transparent @danger;
        border-width: 0 32px 32px 0;
        border-style: solid;
        position: absolute;
        z-index: 1;
      }
      & > span {
        position: relative;
        z-index: 2;
        display: block;
        padding-top: 3px;
      }
    }
  }
  //
  .big-modal {
    .ant-modal {
      max-width: 100%;
      top: 0;
      padding-bottom: 0;
      margin: 0;
    }
    .ant-modal-content {
      display: flex;
      flex-direction: column;
      height: calc(100vh);
    }
    .ant-modal-body {
      flex: 1;
    }
  }
</style>
